import { Box, Typography, CircularProgress } from "@mui/material";
import GymCard from "../../../../Components/GymCard";
import { useGetGymsQuery } from "../../../../Store/api/queries";
import { useState, useEffect, useRef, useMemo } from "react";

const PAGE_SIZE = 10;

export default function GymContent({ queryParams }) {
  const [page, setPage] = useState(1);
  const [allGyms, setAllGyms] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);

  const filterKey = useMemo(() => JSON.stringify(queryParams ?? {}), [queryParams]);

  const { data, isLoading, isFetching, isError } = useGetGymsQuery({
    ...queryParams,
    page,
  });

  const totalCount = data?.count != null ? +data.count : 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 0;

  useEffect(() => {
    setPage(1);
    setAllGyms([]);
    setHasMore(true);
  }, [filterKey]);

  useEffect(() => {
    if (!data || isError) return;

    const pageDocs = data?.data?.docs ?? data?.data ?? [];
    if (!Array.isArray(pageDocs)) return;

    setAllGyms((prev) => {
      if (page === 1) return pageDocs;
      const seen = new Set(prev.map((g) => g._id));
      const next = [...prev];
      for (const gym of pageDocs) {
        if (!seen.has(gym._id)) next.push(gym);
      }
      return next;
    });

    const noMoreByCount = totalPages > 0 ? page >= totalPages : pageDocs.length < PAGE_SIZE;
    setHasMore(!noMoreByCount && pageDocs.length > 0);
  }, [data, page, totalPages, isError]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore || isFetching || isError) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || isFetching || !hasMore) return;
        setPage((p) => p + 1);
      },
      { root: null, rootMargin: "240px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetching, isError, allGyms.length]);

  const isInitialLoading =
    allGyms.length === 0 && (isLoading || (isFetching && page === 1));

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isError && allGyms.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" variant="h6">
          خطا در دریافت اطلاعات باشگاه‌ها. لطفا دوباره تلاش کنید.
        </Typography>
      </Box>
    );
  }

  if (allGyms.length === 0) {
    return (
      <Box
        sx={{
          p: 5,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: 4,
        }}
      >
        <Typography color="text.secondary" variant="h6">
          باشگاهی با این مشخصات یافت نشد!
        </Typography>
      </Box>
    );
  }

  const isLoadingMore = isFetching && page > 1;

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(2, 1fr)",
            xxl: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
          pb: "1rem",
        }}
      >
        {allGyms.map((gym) => (
          <GymCard key={gym._id} gym={gym} />
        ))}
      </Box>

      {/* جای ثابت برای لودر — لیست بالا نپرد */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 56,
          py: 2,
        }}
      >
        {isLoadingMore && <CircularProgress size={32} color="primary" />}
        {!hasMore && !isFetching && (
          <Typography variant="body2" color="text.secondary">
            همه باشگاه‌ها نمایش داده شد
          </Typography>
        )}
      </Box>

      {hasMore && <Box ref={loadMoreRef} sx={{ height: 1, width: "100%" }} aria-hidden />}
    </Box>
  );
}
